import React from "react";
import Icons from "./Icons";

const CONTACT_ICONS = {
  phone: <Icons.Phone />,
  email: <Icons.Mail />,
  location: <Icons.MapPin />,
  linkedin: <Icons.Linkedin />,
  website: <Icons.Link />,
};

const CONTACT_TYPES = [
  {
    value: "phone",
    label: "Phone",
    placeholder: "(555) 123-4567",
    type: "tel",
  },
  {
    value: "email",
    label: "Email",
    placeholder: "john.doe@email.com",
    type: "email",
  },
  {
    value: "location",
    label: "Location",
    placeholder: "New York, NY",
    type: "text",
  },
  {
    value: "linkedin",
    label: "LinkedIn",
    placeholder: "linkedin.com/in/johndoe",
    type: "url",
  },
  {
    value: "website",
    label: "Website",
    placeholder: "www.johndoe.com",
    type: "url",
  },
];

const HeaderEditor = ({ resumeData, updateData }) => {
  const updateHeader = (field, value) => {
    updateData({
      ...resumeData,
      header: {
        ...resumeData.header,
        [field]: value,
      },
    });
  };

  const updateContactInfo = (index, field, value) => {
    const newContactInfo = [...(resumeData.header.contactInfo || [])];
    newContactInfo[index] = {
      ...newContactInfo[index],
      [field]: value,
    };
    updateData({
      ...resumeData,
      header: {
        ...resumeData.header,
        contactInfo: newContactInfo,
      },
    });
  };

  const addContactInfo = (type = "phone") => {
    updateData({
      ...resumeData,
      header: {
        ...resumeData.header,
        contactInfo: [
          ...(resumeData.header.contactInfo || []),
          { type, value: "" },
        ],
      },
    });
  };

  const removeContactInfo = (index) => {
    const newContactInfo = resumeData.header.contactInfo.filter(
      (_, i) => i !== index
    );
    updateData({
      ...resumeData,
      header: {
        ...resumeData.header,
        contactInfo: newContactInfo,
      },
    });
  };

  const taglineLength = resumeData.header.tagline?.length || 0;
  const maxTaglineLength = 100;

  return (
    <div className="header-editor-container">
      {/* Personal Information Card */}
      <div className="editor-card">
        <div className="card-header">
          <h3 className="card-title">
            <Icons.User className="card-icon" />
            Personal Information
          </h3>
        </div>

        <div className="form-group">
          <label className="form-label form-label-required">Full Name</label>
          <input
            type="text"
            value={resumeData.header.name || ""}
            onChange={(e) => updateHeader("name", e.target.value)}
            placeholder="John Doe"
            className="form-input"
            style={{ fontSize: "16px", fontWeight: "600" }}
          />
          <p className="form-helper">
            <Icons.Info className="w-3 h-3" />
            This will be the most prominent text on your resume
          </p>
        </div>

        <div className="form-group">
          <label className="form-label form-label-required">
            Professional Title
          </label>
          <input
            type="text"
            value={resumeData.header.subtitle || ""}
            onChange={(e) => updateHeader("subtitle", e.target.value)}
            placeholder="Senior Software Engineer"
            className="form-input"
          />
          <p className="form-helper">
            <Icons.Info className="w-3 h-3" />
            Your current role or the position you're targeting
          </p>
        </div>

        <div className="form-group">
          <label className="form-label">Professional Tagline</label>
          <textarea
            value={resumeData.header.tagline || ""}
            onChange={(e) => updateHeader("tagline", e.target.value)}
            placeholder="Passionate about building scalable solutions • 10+ years experience • Team leader"
            className="form-textarea"
            rows={2}
            maxLength={maxTaglineLength}
          />
          <div
            className={`char-counter ${taglineLength > 80 ? "warning" : ""} ${
              taglineLength >= maxTaglineLength ? "error" : ""
            }`}
          >
            {taglineLength}/{maxTaglineLength} characters
          </div>
          <p className="form-helper">
            <Icons.Lightbulb className="w-3 h-3" />
            Optional: A brief statement that captures your essence
          </p>
        </div>
      </div>

      {/* Contact Information Card */}
      <div className="editor-card">
        <div className="card-header">
          <h3 className="card-title">
            <Icons.Phone className="card-icon" />
            Contact Information
          </h3>
          <div className="card-actions">
            {(!resumeData.header.contactInfo ||
              resumeData.header.contactInfo.length < 5) && (
              <button
                onClick={() => addContactInfo()}
                className="quick-action-btn"
                style={{ padding: "6px 12px", fontSize: "13px" }}
              >
                <Icons.Plus className="w-3 h-3" />
                Add Contact
              </button>
            )}
          </div>
        </div>

        {!resumeData.header.contactInfo ||
        resumeData.header.contactInfo.length === 0 ? (
          <div className="empty-state-card" style={{ padding: "24px" }}>
            <Icons.Phone
              className="empty-state-icon"
              style={{ width: "48px", height: "48px" }}
            />
            <h4 className="empty-state-title" style={{ fontSize: "16px" }}>
              No Contact Information
            </h4>
            <p className="empty-state-text" style={{ fontSize: "14px" }}>
              Add at least one way for employers to contact you
            </p>
            <button
              onClick={() => addContactInfo()}
              className="empty-state-action"
              style={{ marginTop: "16px" }}
            >
              <Icons.Plus className="w-4 h-4" />
              Add Contact Method
            </button>
          </div>
        ) : (
          <div className="contact-items-list">
            {resumeData.header.contactInfo.map((contact, index) => {
              const contactType = CONTACT_TYPES.find(
                (t) => t.value === contact.type
              );
              return (
                <div key={index} className="contact-item-card">
                  <div className="contact-item-header">
                    <div className="contact-type-badge">
                      {CONTACT_ICONS[contact.type]}
                      <select
                        value={contact.type}
                        onChange={(e) =>
                          updateContactInfo(index, "type", e.target.value)
                        }
                        className="contact-type-select"
                      >
                        {CONTACT_TYPES.map((type) => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <button
                      onClick={() => removeContactInfo(index)}
                      className="contact-remove-btn"
                      title="Remove contact"
                    >
                      <Icons.X className="w-4 h-4" />
                    </button>
                  </div>
                  <input
                    type={contactType?.type || "text"}
                    value={contact.value}
                    onChange={(e) =>
                      updateContactInfo(index, "value", e.target.value)
                    }
                    placeholder={contactType?.placeholder}
                    className="form-input"
                    style={{ marginTop: "8px" }}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// Add Info icon if not present
Icons.Info =
  Icons.Info ||
  (({ className = "w-4 h-4" }) => (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  ));

export default HeaderEditor;
